import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Lightbulb, ArrowRight, Database, Wrench, X } from 'lucide-react';
import { SOLUTION_TYPES, SUGGESTED_TOOLS } from '@/hooks/useChallengeLab';

interface AddSolutionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  challengeId: string;
  challengeTitle: string;
  onSave: (solution: {
    challenge_id: string;
    name: string;
    solution_type: string;
    description: string;
    data_used: string;
    suggested_tools: string[];
    converted_to_project: boolean;
  }) => void;
}

export function AddSolutionDialog({ open, onOpenChange, challengeId, challengeTitle, onSave }: AddSolutionDialogProps) {
  const [formData, setFormData] = useState({ name: '', solutionType: '', description: '', dataUsed: '' });
  const [selectedTools, setSelectedTools] = useState<string[]>([]);
  const [customTool, setCustomTool] = useState('');

  useEffect(() => {
    if (formData.solutionType) {
      setSelectedTools(SUGGESTED_TOOLS[formData.solutionType]?.slice(0, 2) || []);
    }
  }, [formData.solutionType]);

  const handleChange = (field: string, value: string) => setFormData(prev => ({ ...prev, [field]: value }));
  const toggleTool = (tool: string) => setSelectedTools(prev => prev.includes(tool) ? prev.filter(t => t !== tool) : [...prev, tool]);
  const addCustomTool = () => {
    if (customTool.trim() && !selectedTools.includes(customTool.trim())) {
      setSelectedTools(prev => [...prev, customTool.trim()]);
      setCustomTool('');
    }
  };

  const isComplete = !!(formData.name && formData.solutionType && formData.description);

  const handleSave = () => {
    onSave({
      challenge_id: challengeId,
      name: formData.name,
      solution_type: formData.solutionType,
      description: formData.description,
      data_used: formData.dataUsed,
      suggested_tools: selectedTools,
      converted_to_project: false,
    });
    setFormData({ name: '', solutionType: '', description: '', dataUsed: '' });
    setSelectedTools([]);
    onOpenChange(false);
  };

  const suggestedToolsForType = formData.solutionType ? SUGGESTED_TOOLS[formData.solutionType] || [] : [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[650px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Lightbulb className="w-5 h-5 text-secondary" />
            Identificar Solución
          </DialogTitle>
          <DialogDescription>Define una posible solución para el reto: "{challengeTitle}"</DialogDescription>
        </DialogHeader>
        
        <div className="space-y-5 py-4">
          <div className="space-y-2">
            <Label>Nombre del Proyecto Prospecto *</Label>
            <Input placeholder="Ej: Generador Automático de Reportes" value={formData.name} onChange={(e) => handleChange('name', e.target.value)} className="bg-muted/50" />
          </div>
          <div className="space-y-3">
            <Label>Tipo de Solución *</Label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {SOLUTION_TYPES.map((type) => (
                <div key={type.id} className={`p-3 rounded-lg border cursor-pointer transition-all ${formData.solutionType === type.id ? 'border-primary bg-primary/10' : 'border-border hover:border-primary/50 bg-muted/30'}`} onClick={() => handleChange('solutionType', type.id)}>
                  <div className="flex items-center gap-2">
                    <span className="text-xl">{type.icon}</span>
                    <span className="text-sm font-medium">{type.name}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="space-y-2">
            <Label>Descripción de la Solución *</Label>
            <Textarea placeholder="Describe cómo funcionará la solución..." value={formData.description} onChange={(e) => handleChange('description', e.target.value)} className="bg-muted/50 min-h-[80px]" />
          </div>
          <div className="space-y-2">
            <Label className="flex items-center gap-2"><Database className="w-4 h-4 text-muted-foreground" />Datos Utilizados</Label>
            <Textarea placeholder="¿Qué datos necesitará la solución?" value={formData.dataUsed} onChange={(e) => handleChange('dataUsed', e.target.value)} className="bg-muted/50 min-h-[60px]" />
          </div>
          <div className="space-y-3 p-4 rounded-lg bg-muted/30 border border-border/50">
            <Label className="flex items-center gap-2"><Wrench className="w-4 h-4 text-primary" />Herramientas Sugeridas</Label>
            {suggestedToolsForType.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {suggestedToolsForType.map((tool) => (
                  <Badge key={tool} variant={selectedTools.includes(tool) ? "default" : "outline"} className="cursor-pointer" onClick={() => toggleTool(tool)}>
                    {tool}{selectedTools.includes(tool) && <X className="w-3 h-3 ml-1" />}
                  </Badge>
                ))}
              </div>
            )}
            <div className="flex gap-2">
              <Input placeholder="Agregar herramienta..." value={customTool} onChange={(e) => setCustomTool(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addCustomTool())} className="bg-background/50 text-sm" />
              <Button type="button" variant="outline" size="sm" onClick={addCustomTool}>Agregar</Button>
            </div>
            {selectedTools.length > 0 && (
              <div className="pt-2 border-t border-border/50">
                <p className="text-xs text-muted-foreground mb-2">Seleccionadas:</p>
                <div className="flex flex-wrap gap-1">
                  {selectedTools.map((tool) => <Badge key={tool} variant="secondary" className="text-xs">{tool}</Badge>)}
                </div>
              </div>
            )}
          </div>
          <Button onClick={handleSave} disabled={!isComplete} className="w-full gradient-primary" size="lg">
            Guardar Solución<ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
